// context/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Define admin emails - can be from env or hardcoded
  const adminEmails = [
    'admin@example.com',     // Replace with your admin email
    'your-email@example.com' // Add more admin emails as needed
  ];

  // Sign up function
  const signup = async (email, password, displayName, phoneNumber) => {
    try {
      setError('');
      
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update profile with display name
      await updateProfile(user, {
        displayName: displayName
      });
      
      // Set isAdmin based on email
      const isAdmin = adminEmails.includes(email.toLowerCase());
      
      // Store user data in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: email.toLowerCase(),
        displayName: displayName,
        phoneNumber: phoneNumber,
        isAdmin: isAdmin,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      console.log('✅ User created successfully:', user.uid);
      return user;
    } catch (err) {
      console.error('Signup error:', err);
      
      // Handle specific error codes
      if (err.code === 'auth/email-already-in-use') {
        setError('Email already in use. Please login instead.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError(err.message || 'Failed to create account');
      }
      throw err;
    }
  };

  // Login with email and password
  const login = async (email, password) => {
    try {
      setError('');
      
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Fetch user data from Firestore to get additional info
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        // Attach Firestore data to user object
        user.isAdmin = userDoc.data().isAdmin || false;
        user.phoneNumber = userDoc.data().phoneNumber;
      }
      
      console.log('✅ User logged in:', user.uid);
      return user;
    } catch (err) {
      console.error('Login error:', err);
      
      // Handle specific error codes
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError(err.message || 'Failed to login');
      }
      throw err;
    }
  };

  // Login with phone number (optional - for phone + password login)
  const loginWithPhone = async (phoneNumber, password) => {
    try {
      setError('');
      
      // Find user by phone number in Firestore
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('phoneNumber', '==', phoneNumber));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        throw new Error('Phone number not registered');
      }
      
      const userData = snapshot.docs[0].data();
      
      // Login with the associated email
      const userCredential = await signInWithEmailAndPassword(auth, userData.email, password);
      const user = userCredential.user;
      
      // Attach Firestore data
      user.isAdmin = userData.isAdmin || false;
      user.phoneNumber = userData.phoneNumber;
      
      console.log('✅ User logged in with phone:', user.uid);
      return user;
    } catch (err) {
      console.error('Phone login error:', err);
      setError(err.message || 'Failed to login with phone');
      throw err;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setError('');
      await signOut(auth);
      console.log('👋 User logged out');
    } catch (err) {
      console.error('Logout error:', err);
      setError(err.message || 'Failed to logout');
      throw err;
    }
  };

  // Check if a user is admin by userId
  const checkIsAdmin = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data().isAdmin || false;
      }
      return false;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  };

  // Get user profile data
  const getUserProfile = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Update user profile
  const updateUserProfile = async (userId, updates) => {
    try {
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      console.log('✅ User profile updated');
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      return false;
    }
  };

  // Monitor auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Fetch additional user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            // Attach Firestore data to user object
            user.isAdmin = userDoc.data().isAdmin || false;
            user.phoneNumber = userDoc.data().phoneNumber;
            user.firestoreData = userDoc.data();
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
      
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Auth context value
  const value = {
    currentUser,           // Current user object with isAdmin attached
    loading,               // Loading state
    error,                 // Error message
    signup,                // Sign up function
    login,                 // Login with email function
    loginWithPhone,        // Login with phone function
    logout,                // Logout function
    checkIsAdmin,          // Check admin status
    getUserProfile,        // Get user profile
    updateUserProfile,     // Update user profile
    setError               // Set error manually
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};